import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import {
  CalendarClock,
  Check,
  CreditCard,
  Image as ImageIcon,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';

import {
  createBillingSubscription,
  getBillingPlans,
  getBillingStatus,
  keepSubscriptionActive,
  recoverBillingConfirmation,
  scheduleSubscriptionCancellation,
  verifyBillingSubscription,
} from '../utils/api';
import {
  BILLING_ATTEMPT_STORAGE_KEY,
  BILLING_PAYMENT_PENDING_KEY,
  billingStorageKey,
  publishableKeyMatchesMode,
  readStoredBillingValue,
  removeStoredBillingValue,
  secureOperationKey,
  writeStoredBillingValue,
} from '../utils/billingCheckout';
import SubscriptionCheckout from './SubscriptionCheckout';

const BILLING_ATTEMPT_MAX_AGE_MS = 23 * 60 * 60 * 1000;
const BILLING_PAYMENT_PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const BILLING_STATUS_POLL_MS = 5000;
const BILLING_STATUS_POLL_ATTEMPTS = 18;

const SOURCE_LABELS = {
  lifetime: 'Lifetime access',
  paid: 'Paid subscription',
  trial: 'Free trial',
  free: 'Free account',
};

const STATUS_LABELS = {
  incomplete: 'Payment setup incomplete',
  incomplete_expired: 'Payment setup expired',
  trialing: 'Trialing',
  active: 'Active',
  past_due: 'Payment past due',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  paused: 'Paused',
};

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatMoney = (amount, currency) => {
  if (!Number.isInteger(amount) || amount < 0) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
    }).format(amount / 100);
  } catch (_error) {
    return null;
  }
};

const safeErrorMessage = (error, fallback) => (
  error?.data?.message || fallback
);

const SubscriptionPanel = ({
  panelId = 'profile-subscription-panel',
  panelSx = {},
  username = '',
  onAccessGranted,
}) => {
  const [billingStatus, setBillingStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [stripeMode, setStripeMode] = useState(null);
  const [plansError, setPlansError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionStatus, setActionStatus] = useState({ type: '', text: '' });
  const [confirmingCancellation, setConfirmingCancellation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startingPlan, setStartingPlan] = useState('');
  const [selectedPlanKey, setSelectedPlanKey] = useState('');
  const [checkout, setCheckout] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [processingTimedOut, setProcessingTimedOut] = useState(false);
  const mountedRef = useRef(true);
  const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
  const attemptStorageKey = billingStorageKey(
    BILLING_ATTEMPT_STORAGE_KEY,
    username,
  );
  const pendingStorageKey = billingStorageKey(
    BILLING_PAYMENT_PENDING_KEY,
    username,
  );
  const stripeConfigured = publishableKeyMatchesMode(
    publishableKey,
    stripeMode,
  );

  const loadBilling = useCallback(async () => {
    setLoadError('');
    setLoading(true);
    const [statusResult, plansResult] = await Promise.allSettled([
      getBillingStatus(),
      getBillingPlans(),
    ]);
    if (!mountedRef.current) return;

    if (statusResult.status === 'fulfilled') {
      setBillingStatus(statusResult.value);
      const pendingPayment = readStoredBillingValue(pendingStorageKey);
      const pendingIsRecent = (
        pendingPayment?.username === username
        && Number.isFinite(pendingPayment?.startedAt)
        && Date.now() - pendingPayment.startedAt
          <= BILLING_PAYMENT_PENDING_MAX_AGE_MS
      );
      if (
        pendingIsRecent
        && statusResult.value?.subscription?.status === 'incomplete'
      ) {
        setProcessingPayment(true);
        setProcessingTimedOut(false);
      } else if (
        !pendingIsRecent
        || statusResult.value?.subscription?.status !== 'incomplete'
      ) {
        removeStoredBillingValue(pendingStorageKey);
      }
    } else {
      setBillingStatus(null);
      setLoadError(safeErrorMessage(
        statusResult.reason,
        'Subscription details are temporarily unavailable.',
      ));
    }

    if (plansResult.status === 'fulfilled') {
      setPlans(Array.isArray(plansResult.value?.plans) ? plansResult.value.plans : []);
      if (['test', 'live'].includes(plansResult.value?.stripe_mode)) {
        setStripeMode(plansResult.value.stripe_mode);
        setPlansError('');
      } else {
        setStripeMode(null);
        setPlansError('Subscription checkout is not configured correctly.');
      }
    } else {
      setPlans([]);
      setStripeMode(null);
      setPlansError('Plan prices are temporarily unavailable.');
    }
    setLoading(false);
  }, [pendingStorageKey, username]);

  useEffect(() => {
    mountedRef.current = true;
    loadBilling();
    return () => {
      mountedRef.current = false;
    };
  }, [loadBilling]);

  const refreshStatus = async () => {
    const status = await getBillingStatus();
    if (mountedRef.current) setBillingStatus(status);
    return status;
  };

  const handleScheduleCancellation = async () => {
    setSaving(true);
    setActionStatus({ type: '', text: '' });
    try {
      await scheduleSubscriptionCancellation();
      await refreshStatus();
      if (!mountedRef.current) return;
      setConfirmingCancellation(false);
      setActionStatus({
        type: 'success',
        text: 'Your subscription will end after the current paid period.',
      });
    } catch (error) {
      if (!mountedRef.current) return;
      setActionStatus({
        type: 'error',
        text: safeErrorMessage(error, 'Unable to schedule cancellation. Please try again.'),
      });
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const handleKeepSubscription = async () => {
    setSaving(true);
    setActionStatus({ type: '', text: '' });
    try {
      await keepSubscriptionActive();
      await refreshStatus();
      if (!mountedRef.current) return;
      setConfirmingCancellation(false);
      setActionStatus({
        type: 'success',
        text: 'Your subscription will continue renewing.',
      });
    } catch (error) {
      if (!mountedRef.current) return;
      setActionStatus({
        type: 'error',
        text: safeErrorMessage(error, 'Unable to keep the subscription active. Please try again.'),
      });
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const setCheckoutFromResponse = (response, plan) => {
    if (
      response?.status !== 'incomplete'
      || response?.requires_confirmation !== true
      || typeof response?.client_secret !== 'string'
      || !response.client_secret
    ) {
      throw new Error('Secure payment setup returned an invalid response.');
    }
    setCheckout({
      clientSecret: response.client_secret,
      plan,
    });
  };

  const recoverCheckout = async (status = billingStatus) => {
    if (!stripeConfigured) {
      setActionStatus({
        type: 'error',
        text: 'Secure payment setup is not configured correctly.',
      });
      return;
    }
    const planKey = status?.subscription?.plan_key;
    const plan = plans.find((candidate) => candidate.key === planKey);
    if (!plan) {
      setActionStatus({
        type: 'error',
        text: 'The current subscription plan is unavailable.',
      });
      return;
    }

    setStartingPlan(plan.key);
    setActionStatus({ type: '', text: '' });
    try {
      const response = await recoverBillingConfirmation();
      if (!mountedRef.current) return;
      setCheckoutFromResponse(response, plan);
    } catch (error) {
      if (!mountedRef.current) return;
      setActionStatus({
        type: 'error',
        text: safeErrorMessage(
          error,
          'Unable to resume secure payment setup. Please try again.',
        ),
      });
    } finally {
      if (mountedRef.current) setStartingPlan('');
    }
  };

  const createCheckoutSubscription = async (plan) => {
    const storedAttempt = readStoredBillingValue(attemptStorageKey);
    const storedAttemptAge = Date.now() - Number(storedAttempt?.startedAt);
    if (
      storedAttempt?.username === username
      && Number.isFinite(storedAttemptAge)
      && storedAttemptAge > BILLING_ATTEMPT_MAX_AGE_MS
    ) {
      const error = new Error('checkout_attempt_expired');
      error.data = {
        message: 'A previous payment attempt is too old to retry safely. Contact support before trying again.',
      };
      throw error;
    }
    if (
      storedAttempt?.username === username
      && storedAttempt.planKey
      && storedAttempt.planKey !== plan.key
    ) {
      const error = new Error('checkout_plan_conflict');
      error.data = {
        message: `Finish or retry the ${storedAttempt.planKey} payment attempt before choosing another plan.`,
      };
      throw error;
    }

    const operationKey = (
      storedAttempt?.username === username
      && storedAttempt.planKey === plan.key
      && typeof storedAttempt.operationKey === 'string'
    ) ? storedAttempt.operationKey : secureOperationKey();
    if (!operationKey || !writeStoredBillingValue(attemptStorageKey, {
      username,
      planKey: plan.key,
      operationKey,
      startedAt: storedAttempt?.startedAt || Date.now(),
    })) {
      const error = new Error('checkout_storage_unavailable');
      error.data = {
        message: 'Secure payment setup is unavailable in this browser.',
      };
      throw error;
    }

    try {
      const response = await createBillingSubscription(plan.key, operationKey);
      removeStoredBillingValue(attemptStorageKey);
      return response;
    } catch (error) {
      if (error?.data?.code === 'subscription_exists') {
        try {
          const currentStatus = await refreshStatus();
          if (currentStatus?.available_actions?.includes('complete_payment')) {
            const response = await recoverBillingConfirmation();
            removeStoredBillingValue(attemptStorageKey);
            return response;
          }
        } catch (_recoveryError) {
          // Preserve the original safe backend error and operation key for retry.
        }
      }
      throw error;
    }
  };

  const handleOpenCheckout = (plan) => {
    if (!stripeConfigured) {
      setActionStatus({
        type: 'error',
        text: 'Secure payment setup is not configured correctly.',
      });
      return;
    }
    setActionStatus({ type: '', text: '' });
    setCheckout({ clientSecret: null, plan });
  };

  const handlePaymentProcessing = () => {
    setCheckout(null);
    setProcessingPayment(true);
    setProcessingTimedOut(false);
    setActionStatus({
      type: 'info',
      text: 'Payment is processing. Access changes only after Stripe verification.',
    });
  };

  useEffect(() => {
    if (!processingPayment) return undefined;
    let cancelled = false;
    let timeoutId;

    const poll = async () => {
      for (let attempt = 0; attempt <= BILLING_STATUS_POLL_ATTEMPTS; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => {
            timeoutId = window.setTimeout(resolve, BILLING_STATUS_POLL_MS);
          });
        }
        if (cancelled || !mountedRef.current) return;
        try {
          await verifyBillingSubscription();
          if (cancelled || !mountedRef.current) return;
          const status = await getBillingStatus();
          if (cancelled || !mountedRef.current) return;
          setBillingStatus(status);
          if (status?.subscription?.status !== 'incomplete') {
            removeStoredBillingValue(pendingStorageKey);
            setProcessingPayment(false);
            setActionStatus({
              type: status?.entitlement?.source === 'paid' ? 'success' : 'info',
              text: status?.entitlement?.source === 'paid'
                ? 'Payment verified. Full access is active.'
                : 'Payment status changed. Review your subscription details below.',
            });
            return;
          }
        } catch (_error) {
          // A later bounded poll may succeed; no provider details are exposed.
        }
      }
      if (!cancelled && mountedRef.current) {
        removeStoredBillingValue(pendingStorageKey);
        setProcessingPayment(false);
        setProcessingTimedOut(true);
        setActionStatus({
          type: 'info',
          text: 'Payment is still processing. Refresh this page in a few minutes.',
        });
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [pendingStorageKey, processingPayment]);

  const entitlement = billingStatus?.entitlement;
  const accessGrantedRef = useRef(false);
  useEffect(() => {
    if (entitlement?.tier !== 'full') {
      accessGrantedRef.current = false;
      return;
    }
    if (!accessGrantedRef.current) {
      accessGrantedRef.current = true;
      onAccessGranted?.();
    }
  }, [entitlement?.tier, onAccessGranted]);
  const subscription = billingStatus?.subscription;
  const inactivityNotice = billingStatus?.notices?.find(
    (notice) => notice.type === 'inactivity_cancellation',
  );
  const trial = billingStatus?.trial;
  const actions = billingStatus?.available_actions || [];
  const accessLabel = SOURCE_LABELS[entitlement?.source] || 'Subscription status';
  const trialEnd = formatDateTime(trial?.ends_at);
  const periodEnd = formatDateTime(subscription?.period_ends_at);
  const scheduledCancelAt = formatDateTime(subscription?.scheduled_cancel_at);
  const showPlans = actions.includes('subscribe') || actions.includes('restart');
  const selectedPlan = plans.find((plan) => plan.key === selectedPlanKey) || null;
  const selectedAmount = selectedPlan ? formatMoney(selectedPlan.amount, selectedPlan.currency) : null;
  const checkoutAmountLabel = checkout
    ? formatMoney(checkout.plan.amount, checkout.plan.currency)
    : null;
  const checkoutActionAvailable = (
    showPlans || actions.includes('complete_payment')
  );

  return (
    <Paper id={panelId} elevation={0} sx={panelSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CreditCard size={22} aria-hidden="true" />
        <Typography variant="h5" component="h2">Subscription</Typography>
      </Box>

      {loading && (
        <Box
          role="status"
          aria-label="Loading subscription details"
          sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
        >
          <CircularProgress size={30} />
        </Box>
      )}

      {!loading && loadError && (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Alert severity="warning">{loadError}</Alert>
          <Button
            type="button"
            variant="outlined"
            onClick={loadBilling}
            startIcon={<RefreshCw size={17} />}
          >
            Try Again
          </Button>
        </Box>
      )}

      {!loading && billingStatus && (
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              p: 2,
              border: '1px solid rgba(0, 229, 255, 0.45)',
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.12), rgba(18, 24, 22, 0.92))',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <ShieldCheck size={24} color="#39FF14" aria-hidden="true" />
              <Box>
                <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>
                  {accessLabel}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', mb: 0 }}>
                  {entitlement?.tier === 'full' ? 'Full access is enabled.' : 'Core task features remain available.'}
                </Typography>
              </Box>
            </Box>
            {subscription?.status && (
              <Chip
                label={STATUS_LABELS[subscription.status] || subscription.status}
                color={subscription.payment_attention_required ? 'warning' : 'primary'}
                variant="outlined"
              />
            )}
          </Box>

          {entitlement?.source === 'trial' && trialEnd && (
            <Alert severity="info" icon={<CalendarClock size={20} />}>
              Your free trial ends {trialEnd}.
            </Alert>
          )}

          {subscription?.payment_attention_required && (
            <Alert severity="warning">
              Your subscription needs payment attention. No card details are stored by Dopatize.
            </Alert>
          )}

          {inactivityNotice && (
            <Alert severity="warning">
              Your subscription is scheduled to end for inactivity on{' '}
              {formatDateTime(inactivityNotice.period_ends_at)}. Sign in before then to keep it active.
            </Alert>
          )}

          {subscription && (
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', mb: 0 }}>
                Plan: {subscription.plan_key === 'yearly' ? 'Yearly' : 'Monthly'}
              </Typography>
              {periodEnd && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', mb: 0 }}>
                  {subscription.cancellation_scheduled ? 'Paid access through' : 'Current period ends'}: {periodEnd}
                </Typography>
              )}
            </Box>
          )}

          {subscription?.cancellation_scheduled && (
            <Alert severity="warning">
              Cancellation is scheduled{scheduledCancelAt ? ` for ${scheduledCancelAt}` : ' for the end of the paid period'}.
              You keep access until then.
            </Alert>
          )}

          {actionStatus.text && (
            <Alert severity={actionStatus.type}>{actionStatus.text}</Alert>
          )}

          {checkoutActionAvailable && stripeMode && !stripeConfigured && (
            <Alert severity="error">
              Secure payment setup is unavailable because its environment configuration does not match this deployment.
            </Alert>
          )}

          {processingPayment && (
            <Box
              role="status"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                border: '1px solid rgba(0, 229, 255, 0.45)',
                borderRadius: 3,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', mb: 0 }}>
                Waiting for verified payment status…
              </Typography>
            </Box>
          )}

          {processingTimedOut && (
            <Button type="button" variant="outlined" onClick={loadBilling}>
              Refresh Payment Status
            </Button>
          )}

          {!checkout
            && !processingPayment
            && actions.includes('complete_payment') && (
            <Button
              type="button"
              variant="contained"
              onClick={() => recoverCheckout()}
              disabled={!stripeConfigured || Boolean(startingPlan)}
            >
              {startingPlan ? 'Loading Secure Payment...' : 'Continue Payment'}
            </Button>
          )}

          {checkout && checkoutAmountLabel && stripeConfigured && (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                p: { xs: 1.5, sm: 2 },
                border: '1px solid rgba(57, 255, 20, 0.52)',
                borderRadius: 3,
                backgroundColor: 'rgba(57, 255, 20, 0.04)',
              }}
            >
              {!checkout.clientSecret && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setCheckout(null)}
                  sx={{ justifySelf: 'start' }}
                >
                  Change Plan
                </Button>
              )}
              <Alert severity="info">
                Dopatize will cancel your subscription if your account is inactive for a month. You can restart your subscription at any time.
              </Alert>
              <SubscriptionCheckout
                amount={checkout.plan.amount}
                amountLabel={checkoutAmountLabel}
                clientSecret={checkout.clientSecret}
                createSubscription={() => createCheckoutSubscription(checkout.plan)}
                currency={checkout.plan.currency}
                planKey={checkout.plan.key}
                publishableKey={publishableKey}
                username={username}
                onProcessing={handlePaymentProcessing}
              />
            </Box>
          )}

          {confirmingCancellation && (
            <Alert severity="warning">
              <Typography variant="body2" sx={{ color: 'inherit', mb: 1.25 }}>
                Confirm cancellation at the end of the current paid period. Access will not end immediately.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  type="button"
                  color="warning"
                  variant="contained"
                  onClick={handleScheduleCancellation}
                  disabled={saving}
                >
                  {saving ? 'Scheduling...' : 'Confirm Cancellation'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => setConfirmingCancellation(false)}
                  disabled={saving}
                >
                  Go Back
                </Button>
              </Box>
            </Alert>
          )}

          {!confirmingCancellation && actions.includes('cancel') && (
            <Button
              type="button"
              color="warning"
              variant="outlined"
              onClick={() => {
                setActionStatus({ type: '', text: '' });
                setConfirmingCancellation(true);
              }}
              disabled={saving}
            >
              Cancel at Period End
            </Button>
          )}

          {actions.includes('keep_subscription') && (
            <Button
              type="button"
              variant="contained"
              onClick={handleKeepSubscription}
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Keep Subscription'}
            </Button>
          )}

          {showPlans && !checkout && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Box>
                <Typography variant="h6" align="center" sx={{ color: 'primary.main', mb: 0.75 }}>
                  Choose your plan
                </Typography>
              </Box>
              <Box
                role="radiogroup"
                aria-label="Subscription billing plan"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gridAutoRows: '1fr',
                  gap: 1.5,
                }}
              >
                {plans.map((plan) => {
                  const amount = formatMoney(plan.amount, plan.currency);
                  const isSelected = selectedPlanKey === plan.key;
                  const annualSavings = formatMoney(plan.annual_savings, plan.currency);
                  const monthlyEquivalent = plan.interval === 'year'
                    ? formatMoney(Math.round(plan.amount / 12), plan.currency)
                    : null;
                  return (
                    <Box
                      key={plan.key}
                      component="button"
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelectedPlanKey(plan.key)}
                      disabled={!stripeConfigured || Boolean(startingPlan) || processingPayment}
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        minHeight: { xs: 210, sm: 230 },
                        p: { xs: 2, sm: 2.25 },
                        border: isSelected
                          ? '2px solid #00E5FF'
                          : '2px solid rgba(255,255,255,0.18)',
                        borderRadius: 4,
                        background: isSelected
                          ? 'linear-gradient(145deg, rgba(0,229,255,0.18), rgba(18,24,22,0.96))'
                          : 'linear-gradient(145deg, rgba(255,255,255,0.055), rgba(18,24,22,0.96))',
                        boxShadow: isSelected
                          ? '0 0 0 2px rgba(0,229,255,0.12), 0 8px 0 rgba(0,92,102,0.58), 0 0 24px rgba(0,229,255,0.16)'
                          : '0 6px 0 rgba(0,0,0,0.38)',
                        color: 'inherit',
                        font: 'inherit',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
                        '&:hover:not(:disabled)': {
                          transform: 'translate(-2px, -2px)',
                          borderColor: '#00E5FF',
                        },
                        '&:active:not(:disabled)': {
                          transform: 'translateY(3px)',
                          boxShadow: '0 2px 0 rgba(0,92,102,0.58)',
                        },
                        '&:focus-visible': {
                          outline: '3px solid rgba(57,255,20,0.75)',
                          outlineOffset: 4,
                        },
                        '&:disabled': {
                          cursor: 'not-allowed',
                          opacity: 0.55,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ color: isSelected ? 'green.main' : 'white' }}>
                          {plan.display_name}
                        </Typography>
                        <Box
                          aria-hidden="true"
                          sx={{
                            width: 28,
                            height: 28,
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '50%',
                            border: isSelected ? '2px solid #00E5FF' : '2px solid rgba(255,255,255,0.28)',
                            backgroundColor: isSelected ? '#00E5FF' : 'transparent',
                            color: '#090D0E',
                          }}
                        >
                          {isSelected && <Check size={18} strokeWidth={3} />}
                        </Box>
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Typography component="span" variant="h4" sx={{ color: 'secondary.main' }}>
                          {amount || 'Price unavailable'}
                        </Typography>
                        {amount && (
                          <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', ml: 0.75 }}>
                            / {plan.interval}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ mt: 'auto', pt: 2, minHeight: 62 }}>
                        {plan.annual_savings_percent > 0 ? (
                          <>
                            <Chip
                              label={`Save ${plan.annual_savings_percent}%`}
                              size="small"
                              color="success"
                              sx={{ mb: 0.75, fontWeight: 700 }}
                            />
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {[
                                annualSavings ? `Save ${annualSavings} each year.` : '',
                                monthlyEquivalent ? `${monthlyEquivalent} per month.` : '',
                              ].filter(Boolean).join(' ')}

                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption">
                            Billed monthly. Cancel before a future renewal.
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.62)', mt: 1.25 }}>
                        {amount ? `${amount} charged today, then every ${plan.interval}.` : 'Price unavailable.'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  p: { xs: 1.75, sm: 2 },
                  borderRadius: 3,
                  border: '1px solid rgba(57,255,20,0.34)',
                  backgroundColor: 'rgba(57,255,20,0.045)',
                }}
              >
                <Typography variant="subtitle1" align="center">
                  Included with either plan
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <ImageIcon size={20} color="#39FF14" aria-hidden="true" />
                  <Typography variant="body1" sx={{ mb: 0, }}>
                    Unlimited AI images
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Sparkles size={20} color="#39FF14" aria-hidden="true" />
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Unlimited AI task generations
                  </Typography>
                </Box>
              </Box>

              <Box
                aria-live="polite"
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 3,
                  backgroundColor: selectedPlan ? 'rgba(0,229,255,0.07)' : 'rgba(255,255,255,0.035)',
                }}
              >
                <Typography variant="body2" sx={{ mb: 0, color: selectedPlan ? 'white' : 'rgba(255,255,255,0.66)' }}>
                  {selectedPlan && selectedAmount
                    ? `${selectedPlan.display_name} selected — ${selectedAmount} will be charged today.`
                    : 'Select Monthly or Yearly above to continue.'}
                </Typography>
                <Button
                  type="button"
                  variant="contained"
                  fullWidth
                  disabled={!selectedPlan || !stripeConfigured || Boolean(startingPlan) || processingPayment}
                  onClick={() => selectedPlan && handleOpenCheckout(selectedPlan)}
                >
                  {startingPlan
                    ? 'Opening Secure Payment...'
                    : selectedPlan
                      ? `Continue with ${selectedPlan.display_name}`
                      : 'Select a plan to continue'}
                </Button>
                <Typography variant="caption" sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  Next, enter your card securely with Stripe. You can review the amount before paying.
                </Typography>
              </Box>
              {plansError && <Alert severity="warning">{plansError}</Alert>}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default SubscriptionPanel;
